import React from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import type { AnnouncementPayload } from '../../types/announcement';
import { useTranslation } from 'react-i18next';

interface AnnouncementModalProps {
  visible: boolean;
  announcement: AnnouncementPayload | null;
  onDismiss: () => void;
  onCTA: (route: string) => void;
}

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  visible,
  announcement,
  onDismiss,
  onCTA,
}) => {
  const { t } = useTranslation();
  if (!announcement) return null;

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onDismiss}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          {!!announcement.icon && (
            <View style={styles.iconContainer}>
              <Feather name={announcement.icon as any} size={28} color={Paprika.accent} />
            </View>
          )}
          <Text style={styles.title}>{announcement.title}</Text>
          <Text style={styles.body}>{announcement.body}</Text>

          <View style={styles.buttons}>
            <TouchableOpacity style={styles.dismissButton} onPress={onDismiss}>
              <Text style={styles.dismissButtonText}>{t('common.gotIt')}</Text>
            </TouchableOpacity>

            {!!announcement.cta && !!announcement.ctaRoute && (
              <TouchableOpacity
                style={styles.ctaButton}
                onPress={() => onCTA(announcement.ctaRoute!)}
              >
                <Text style={styles.ctaButtonText}>{announcement.cta}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    marginHorizontal: PaprikaSpacing.lg,
    minWidth: 280,
    maxWidth: 340,
    alignItems: 'center',
    ...PaprikaShadows.modal,
  },
  iconContainer: {
    marginBottom: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLight,
    borderRadius: 50,
    padding: PaprikaSpacing.md,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  body: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: PaprikaSpacing.lg,
  },
  buttons: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
    width: '100%',
  },
  dismissButton: {
    flex: 1,
    backgroundColor: Paprika.surfaceLight,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  dismissButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  ctaButton: {
    flex: 1,
    backgroundColor: Paprika.accent,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
  },
  ctaButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },
});
